import Api from 'entities/helpers/api';
import PropTypes from 'prop-types';
import ProposalFormController from '../proposal-form-controller';
import useProposalFormFluxStore from '../hooks/use-proposal-form-flux-store';
import { useEffect, useState, useContext, createContext } from 'react';
import { mapSnakeToCamel } from 'apps/home/utils/helpers';

/**
 * For some reason, Proposal Custom Fields are returned from the API in snake_case, whereas Interaction Custom Fields
 * are returned in camelCase. This is a mapping to convert the snake_case fields to camelCase.
 * This is used in the CustomFields component to map the custom fields from the API to the props that the component expects.
 */
const MAPPINGS = {
  id: 'id',
  oid: 'oid',
  data_type: 'dataType',
  ui_control_type: 'uiControlType',
  required: 'required',
  display_name: 'displayName',
  deleted: 'deleted',
  sort_order: 'sortOrder',
  active: 'active',
  created_at: 'createdAt',
  updated_at: 'updatedAt',
  values: 'values',
};

const ProposalFormContext = createContext(() => null);

const ProposalFormProvider = ({ children }) => {
  const fluxStoreState = useProposalFormFluxStore();

  const [rules, setRules] = useState([]);
  const [customFields, setCustomFields] = useState([]);

  useEffect(() => {
    const fetchCustomFields = async () => {
      const fields = (await Api.UGC.PROPOSAL_CUSTOM_FIELDS.get()) || [];
      const mappedFields = fields.map((field) => mapSnakeToCamel(field, MAPPINGS));
      setCustomFields(mappedFields);
    };
    if (fluxStoreState.hasCustomFields) {
      fetchCustomFields();
    }
  }, [fluxStoreState.hasCustomFields]);

  useEffect(() => {
    const getAndSetRules = async () => {
      try {
        const rulesFromApi = await ProposalFormController.fetchRules();
        const dnaRules = ProposalFormController.mapDnaToFieldTriggerRules(fluxStoreState.standardFieldsConfig);
        const customFieldRules = ProposalFormController.mapCustomFieldsToRules(
          fluxStoreState.hasCustomFields,
          customFields
        );
        const combinedRules = [...rulesFromApi, ...dnaRules, ...customFieldRules];
        setRules(combinedRules);
      } catch (error) {
        console.error('Error fetching rules:', error);
        setRules([]); // Set to empty array on error to avoid breaking the form
      }
    };
    getAndSetRules();
  }, [fluxStoreState.standardFieldsConfig, fluxStoreState.hasCustomFields, customFields]);

  return (
    <ProposalFormContext.Provider
      value={{
        ...fluxStoreState,
        rules,
        customFields,
      }}
    >
      {children}
    </ProposalFormContext.Provider>
  );
};

ProposalFormProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useProposalFormContext = () => {
  const context = useContext(ProposalFormContext);
  if (!context) {
    throw new Error('useProposalFormContext must be used within a ProposalFormProvider');
  }
  return context;
};

export default ProposalFormProvider;
